import { INotificationType } from '@common';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { selectNotifications } from '@store/selectors';
import { VehicleUnavailableNotification } from './VehicleUnavailableNotification';
import { clearNotifications } from '@store/slices/salesWizardSlice';
import { ErrorNotification, CustomErrorNotification } from './ErrorNotification';
import { ReservedWarning } from './ReservedWarning';
import { IconType, MessageBar } from '@repo/ui/components';
import { FactoryCodeWarning } from './FactoryCodeWarning';
import { NextErrorNotification } from './NextErrorNotification';
import { ServiceContractWarning } from './FinanceWarning';
import { PriceMismatchWarning } from './PriceMismatchWarning';
import { InterestWarning } from './IntererstWarning';
import { CustomerValidationWarning } from './CustomerValidationWarning';
import { ProdDateWarning } from './ProdDateWarning';
import { TMessageBarSeverity } from 'types';

export const CommonNotifications = () => {
  const storeNotifications = useAppSelector(selectNotifications);
  const dispatch = useAppDispatch();
  let notifications: JSX.Element[] = [];

  storeNotifications.forEach((notification) => {
    const commonNotificationProps = {
      key: notification?.key,
      errorCode: notification?.errorCode,
      severity: notification?.severityLevel,
      hideLink: notification?.hideLink,
      className: notification?.className,
      onClose: () => notification?.onClose || dispatch(clearNotifications(notification?.key)),
      ...(notification?.severityLevel === TMessageBarSeverity.ERROR && {
        alertIcon: { icon: IconType.NewNextWarningSolid, size: 32 },
      }),
    };
    switch (notification?.type) {
      case INotificationType.PIM_DATA_UNAVAILABLE: {
        notifications.push(<VehicleUnavailableNotification {...commonNotificationProps} />);
        break;
      }
      case INotificationType.PRESELECTION_ERROR:
      case INotificationType.OFFER_CREATION_ERROR:
      case INotificationType.ONE_OFF_TAX_UNAVAILABLE:
      case INotificationType.DISCOUNT_CHANGED:
      case INotificationType.CUSTOMER_ERROR:
      case INotificationType.REMOVE_LEASING:
      case INotificationType.VEHICLE_MISSING_WARNING:
      case INotificationType.RESERVED_CAR:
      case INotificationType.VAT_ERROR:
      case INotificationType.VEHICLE_STOCK_CONFIGURATION_ERROR:
      case INotificationType.FACTORY_ORDER_WARNING:
      case INotificationType.VEHICLE_VALIDATION_ERROR: {
        notifications.push(<ErrorNotification {...commonNotificationProps} />);
        break;
      }
      case INotificationType.INVALID_TIME_VALUE: {
        notifications.push(<CustomErrorNotification {...commonNotificationProps} title={notification?.title} />);
        break;
      }
      case INotificationType.RESERVED_WARNING: {
        notifications.push(
          <ReservedWarning
            {...commonNotificationProps}
            message={notification?.message}
            serialNo={notification?.serialNo}
          />
        );
        break;
      }
      case INotificationType.STOCK_SALE_VEHICLE_STOCK_CONFIGURATION_ERROR:
      case INotificationType.FILE_SELECTION_ERROR:
      case INotificationType.VEHICLE_ORDER_DRAFT_ERROR: {
        notifications.push(
          <MessageBar {...commonNotificationProps} dismissible>
            <span className="text-base/[175%] font-semibold">{notification?.title}</span>
          </MessageBar>
        );
        break;
      }
      case INotificationType.SHARE_CODE_UPDATE_FAILED: {
        notifications.push(
          <MessageBar
            {...commonNotificationProps}
            severity={notification?.severityLevel}
            dismissible
            alertIcon={{ icon: IconType.NewNextWarningSolid, size: 32 }}
            className="w-[21rem] border-0 py-8 gap-6 rounded-lg [&_.tw-dismiss-button]:-top-4"
          >
            <p className="text-base/[175%] font-semibold">{notification?.title}</p>
          </MessageBar>
        );
        break;
      }
      case INotificationType.FACTORY_CODE_UNAVAILABLE: {
        notifications.push(<FactoryCodeWarning {...commonNotificationProps} />);
        break;
      }
      case INotificationType.RESERVED_FAIL:
      case INotificationType.REMOVE_VEHICLE_FAIL:
      case INotificationType.CHOOSE_OFFER_FAIL:
      case INotificationType.TOGGLE_ACCESSORY_FAIL:
      case INotificationType.ADD_CALCULATION_FAIL:
      case INotificationType.REMOVE_CALCULATION_FAIL:
      case INotificationType.EXECUTE_SIGN_FAIL:
      case INotificationType.UPDATE_BRUKS_CODE_FAIL:
      case INotificationType.TOGGLE_PCODES_FAIL:
      case INotificationType.RECALCULATION_FAIL:
      case INotificationType.VEHICLE_ADD_ERROR:
      case INotificationType.INVALID_VEHICLE_STOCK_STATUS_ERROR:
      case INotificationType.ADD_VEHICLE_ERROR:
      case INotificationType.SHARE_CODE_ERROR: {
        notifications.push(<NextErrorNotification {...commonNotificationProps} orderId={notification?.orderId} />);
        break;
      }
      case INotificationType.SERVICE_CONTRACT_WARNING: {
        notifications.push(
          <ServiceContractWarning {...commonNotificationProps} validationErrors={notification?.message} />
        );
        break;
      }
      case INotificationType.PRICE_CHANGE: {
        notifications.push(
          <PriceMismatchWarning {...commonNotificationProps} priceChangedFields={notification?.priceChangedFields} />
        );
        break;
      }
      case INotificationType.INTEREST_WARNING: {
        notifications.push(<InterestWarning {...commonNotificationProps} offer={notification?.chosenOffer} />);
        break;
      }
      case INotificationType.CUSTOMER_WARNING: {
        notifications.push(<CustomerValidationWarning {...commonNotificationProps} fields={notification?.fields} />);
        break;
      }
      case INotificationType.PROD_DATE_WARNING: {
        notifications.push(<ProdDateWarning {...commonNotificationProps} invalidItems={notification?.invalidItems} />);
        break;
      }
      default:
        break;
    }
  });

  return <>{notifications?.map((notification) => notification)}</>;
};
